import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'preact/hooks';

import { patchUserMutationFn } from '@/queries/users';
import { useSelector } from '@/redux/helper';
import { selectTourGate, selectToursReady } from '@/redux/slices/tours/selectors';

import toursSyncPayload, { toursSyncDelta } from '../persistence/sync';
import { TourAnalytics, TourAnalyticsPatch, TourGateState } from '../types';

/** Coalesce bursts of step advances into a single request. */
const SYNC_DEBOUNCE_MS = 2000;

/**
 * Pushes durable tour analytics to the user object. Mounted once, globally (outside the
 * Router) — TourHost renders in more than one place, so syncing from there would double-send.
 *
 * Only the analytics travel; the step history stays local. Only the fields that actually
 * changed travel, so idle navigation costs no requests and a restart's clears are explicit.
 *
 * Requests are serialized through a promise chain so an older in-flight PATCH cannot overwrite
 * a newer one (or move the local baseline backwards) when the user advances quickly.
 */
const TourSync = () => {
  // Nothing is uploaded until the session resolves. Two reasons, and neither is optional:
  // `user.tours` only arrives with the session, and until it does the merged view below is
  // client-only — so an early send would push this device's copy over a fresher server record.
  const isReady = useSelector(selectToursReady);
  // The MERGED view, not the raw client slice: its monotonic guards are what make a sync
  // incapable of moving server-side progress backwards.
  const gate = useSelector(selectTourGate);
  const serverTours = useSelector((state) => state.user.tours);
  const userId = useSelector((state) => state.user.userId);

  const { mutateAsync } = useMutation({
    mutationFn: (tours: Record<string, TourAnalyticsPatch>) => patchUserMutationFn({ tours }),
  });

  // What the server is believed to hold. Seeded from `user.tours` once the session lands, and
  // advanced only by a confirmed PATCH — so a failed send retries on the next change, and a
  // successful one is never re-sent.
  const baselineRef = useRef<Record<string, TourAnalytics> | undefined>(undefined);
  const gateRef = useRef<TourGateState>(gate);
  gateRef.current = gate;
  // Serialize PATCHes: each flush awaits the previous so out-of-order responses cannot regress.
  const chainRef = useRef(Promise.resolve());
  // Bumped when the session drops so an in-flight PATCH cannot restore the previous user's baseline.
  const generationRef = useRef(0);
  /** Whose baseline `baselineRef` holds. See the ownership check at the top of the effect. */
  const baselineUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!isReady) {
      baselineRef.current = undefined; // a different user must not inherit this baseline
      baselineUserIdRef.current = undefined;
      generationRef.current += 1;
      return undefined;
    }

    /**
     * Drop a baseline belonging to somebody else, before anything is computed against it.
     *
     * Dropping the session is not the only way the user changes. On a switch the session never
     * ends — `UserSessionSync` receives the new user and `logged` stays true throughout — so
     * `isReady` never goes false and the guard above never fires. The baseline stayed seeded from
     * the previous user, `selectTourGate` merged their client progress over the new user's server
     * record, and the delta below wrote one person's tour progress onto the other's account.
     *
     * Checked here rather than in a `useEffect` of its own: effects run in declaration order, so a
     * separate one would be correct only until somebody reordered the hooks — a silent breakage
     * guarding a bug that corrupts another user's data.
     */
    if (baselineUserIdRef.current !== userId) {
      baselineRef.current = undefined;
      baselineUserIdRef.current = userId;
      generationRef.current += 1;
    }

    if (!baselineRef.current) baselineRef.current = serverTours ?? {};

    const delta = toursSyncDelta(gate.tours, baselineRef.current);
    if (Object.keys(delta).length === 0) return undefined;

    const generation = generationRef.current;
    const timer = setTimeout(() => {
      chainRef.current = chainRef.current
        .catch(() => undefined) // keep the chain alive after a failure
        .then(async () => {
          if (generationRef.current !== generation) return;
          // Recompute against the latest gate + baseline at send time, not the debounce snapshot.
          const latest = gateRef.current;
          const nextDelta = toursSyncDelta(latest.tours, baselineRef.current);
          if (Object.keys(nextDelta).length === 0) return;
          const confirmed = toursSyncPayload(latest.tours);
          await mutateAsync(nextDelta);
          if (generationRef.current !== generation) return;
          baselineRef.current = confirmed;
        });
    }, SYNC_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [isReady, gate, serverTours, userId, mutateAsync]);

  return null;
};

export default TourSync;
